import { type LoginRequest, loginRequestSchema, type LoginResponse } from '@dnx/contracts';
import { Alert, Button, Field, Input, PasswordInput } from '@dnx/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { AuthLayout } from '../components/auth-layout.js';
import { api, ApiError } from '../lib/api.js';
import { pendingStep } from '../lib/session.js';

export function LoginPage() {
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors } } = useForm<LoginRequest>({
    resolver: zodResolver(loginRequestSchema),
    defaultValues: { email: '', password: '' },
  });

  const login = useMutation({
    mutationFn: (body: LoginRequest) => api<LoginResponse>('/auth/login', { method: 'POST', body }),
    onSuccess: (res) => {
      pendingStep.set(res.next);
      void navigate({ to: res.next === 'mfa_setup' ? '/entrar/autenticador' : '/entrar/verificacao' });
    },
  });

  return (
    <AuthLayout
      title="Entrar no painel"
      description="Use o e-mail cadastrado pela sua clínica."
      footer="Acesso exclusivo da equipe. Pacientes acessam pelo link enviado pela clínica."
    >
      <form className="flex flex-col gap-5" noValidate onSubmit={handleSubmit((v) => login.mutate(v))}>
        {login.error && (
          <Alert tone="danger" title={login.error instanceof ApiError && login.error.code === 'RATE_LIMITED' ? 'Acesso temporariamente bloqueado' : 'Não foi possível entrar'}>
            {login.error.message}
          </Alert>
        )}
        <Field label="E-mail" error={errors.email?.message}>
          <Input type="email" autoComplete="username" autoFocus placeholder="nome@clinica.com.br" {...register('email')} />
        </Field>
        <Field
          label="Senha"
          error={errors.password?.message}
          labelAction={<Link to="/entrar/esqueci" className="text-xs font-medium text-primary hover:underline">Esqueci minha senha</Link>}
        >
          <PasswordInput autoComplete="current-password" {...register('password')} />
        </Field>
        <Button type="submit" size="lg" block loading={login.isPending}>Entrar</Button>
      </form>
    </AuthLayout>
  );
}
